import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useI18n } from "@/contexts/LanguageContext";

export default function ProtectedRoute({ children, skipOnboardingGuard = false }) {
  const { user, loading } = useAuth();
  const { settings, loading: settingsLoading, refresh, loadError } = useSettings();
  const { t } = useI18n();
  const location = useLocation();

  if (loading || (user && settingsLoading && !settings)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="font-mono text-xs uppercase tracking-widest text-steel">{t.common.loading}</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  // Recoverable fetch failure: allow app shell (banner in Layout) and /settings retry UI.
  const settingsBlocking = !settingsLoading && !settings && !loadError;

  if (!skipOnboardingGuard && settingsBlocking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper p-4">
        <div className="brutal-card p-6 max-w-md text-center">
          <div className="font-display font-black text-xl tracking-tight mb-2">{t.common.settingsUnavailableTitle}</div>
          <div className="text-sm text-steel mb-4">
            {t.common.settingsUnavailableBody}
          </div>
          <button
            type="button"
            onClick={() => void refresh()}
            className="px-4 py-2 bg-ink text-white border-2 border-ink font-bold uppercase tracking-wider text-xs"
            data-testid="protected-settings-retry"
          >
            {t.common.retry}
          </button>
        </div>
      </div>
    );
  }

  // Onboarding is activation, not authorization. Suggest it from dashboard only,
  // so deep links like /measurements cannot trap a new beta user in setup.
  const onboardingSkipped = sessionStorage.getItem("factlify_onboarding_skipped") === "1";
  if (
    !skipOnboardingGuard &&
    !loadError &&
    !onboardingSkipped &&
    location.pathname === "/dashboard" &&
    settings &&
    settings.onboarded !== true
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}
